import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Image,
  Navbar,
  Row,
} from "react-bootstrap";
import login_img from "../../assets/images/login-img-new.svg";
import "./login.css";
import { Link, useNavigate } from "react-router-dom";
import { useLocalizedRoutes } from "../../hooks/useLocalizedRoutes";
import Feedback from "react-bootstrap/esm/Feedback";
import { useTranslation } from "react-i18next";
import { MiniLoader } from "../ui/MiniLoader";
import { HelpInfo } from "../ui/HelpInfo";
import axiosApi from "../../axios/axios-api";
import PetroprixLogo from "../../assets/images/petroprix-logo";
import { getRecaptchaLanguage } from "../../helpers/recaptchaLanguage";
import { LocalizedRecaptcha } from "../ui/LocalizedRecaptcha";

const URL_REGISTRO = "login/validar";

export const RegistroScreen = () => {
  const [validated, setValidated] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(false);
  const [numLogins, setNumLogins] = useState(0);
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITEKEY;
  const [captchaValid, setCaptchaValid] = useState(null);
  const captchaRef = useRef(null);
  const [messageFeedback, setMessageFeedback] = useState("");
  const { t, i18n } = useTranslation();
  const { getPath } = useLocalizedRoutes();
  const captchaLanguage = getRecaptchaLanguage(
    i18n.resolvedLanguage || i18n.language
  );

  const [telefono, setTelefono] = useState(t("ayuda.telefono"));
  const [email, setEmail] = useState(t("ayuda.email"));

  const [formData, setFormData] = useState({
    username: "",
  });

  const [formErrors, setFormErrors] = useState({
    username: false,
  });

  const navigate = useNavigate();

  const onChange = () => {
    if (captchaRef.current.getValue()) {
      setCaptchaValid(true);
    } else {
      setCaptchaValid(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setLoading(true);
    setMessageFeedback("");
    setValidated(false);
    setErrors(false);
    setNumLogins(numLogins + 1);
    setFormErrors({ username: false });

    if (!validatedEmail(formData.username.trim())) {
      setFormErrors({ username: true });
      setValidated(true);
      setLoading(false);
      return;
    }

    setValidated(true);

    if (!captchaValid) {
      setCaptchaValid(false);
      setLoading(false);
      return;
    }

    let data = {
      email: formData.username.trim(),
      captcha: captchaRef.current.getValue(),
    };

    try {
      if (process.env.REACT_APP_URL_API) {
        const response = await axiosApi.post(URL_REGISTRO, data, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        const backendMessage = response?.data?.message;
        const translatedMessage =
          backendMessage === "Customer already registered"
            ? t("register.message.customer-already-registered")
            : backendMessage === "Validation Error"
            ? t("register.message.validation-error")
            : backendMessage === "Active link, check email"
            ? t("register.message.active-link-check-email")
            : backendMessage;

        if (response?.data?.code === 200) {
          setValidated(false);
          setFormData({ username: "" });
          setCompleted(true);
          setErrors(false);
          
          setMessageFeedback(translatedMessage);
          setLoading(false);
          //Navegamos a la pantalla de envio de email y pasamos por props el email
          navigate(getPath('registerVerify'), {
            state: { email: data.email },
          });
        } else {
          setErrors(true);
          setCompleted(false);
          setMessageFeedback(translatedMessage);
          setCaptchaValid(null);
          setLoading(false);
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
        }
      }
    } catch (error) {
      setValidated(false);
      setErrors(true);
      setMessageFeedback(error.message);
      setCompleted(false);
      setCaptchaValid(null);
      setLoading(false);
    } finally {
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    }
  };

  const validatedEmail = (email) => {
    const re = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    return () => {
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    };
  }, []);

  const fetchInfoContacto = useCallback(async () => {
    try {
      if (process.env.REACT_APP_URL_API) {
        const response = await axiosApi.get("infoPetroprix", {
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (response?.data?.code === 200) {
          let data = response.data.data;
          setTelefono(data.telefono);
          setEmail(data.email_informacion);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInfoContacto();
  }, []);

  return (
    <>
      <Container className="d-lg-none d-xl-none">
        <Navbar
          collapseOnSelect
          expand="lg"
          className="menu px-1"
          style={{ display: "flex" }}
        >
          <Navbar.Brand
            as={Link}
            to={getPath('login')}
            className="d-flex flex-column align-items-start"
          >
            <PetroprixLogo className="d-inline-block align-left login-page-logo" />
            <Col xs={12} lg={{ span: 12, order: 1 }}>
              <h3>
                {t("login.header.h1")}
                {t("login.header.h1.red")}
              </h3>
            </Col>
          </Navbar.Brand>
          <HelpInfo telefono_informacion={telefono} email={email} />
        </Navbar>
      </Container>
      <Container className="content-login-page">
        <Col xs={12} lg={{ span: 11, offset: 1 }}>
          <Container className="login-header-container">
            <header className="d-none d-lg-block">
              <PetroprixLogo className="d-inline-block align-left login-page-logo" />
              <Col xs={12} lg={{ span: 12, order: 1 }}>
                <h3>
                  {t("login.header.h1")}
                  {t("login.header.h1.red")}
                </h3>
              </Col>
            </header>
            <HelpInfo
              className={"d-none d-lg-block"}
              telefono_informacion={telefono}
              email={email}
            />
          </Container>
        </Col>
        <Container className="login-container py-1">
          <Row className="content-section login-section">
            <Col xs={12} lg={{ span: 4, offset: 2, order: 3 }} className="mt-5">
              <Link to={getPath('login')} className="back-link">
                {t("ui.back-button")}
              </Link>{" "}
              <h2 className="mt-2 text-center">{t("register.h2")}</h2>
              {completed && !errors && (
                <Alert variant="success-login">{messageFeedback}</Alert>
              )}
              {!completed && !errors && numLogins === 0 && (
                <Alert variant="primary-login">
                  {t("register.alert")}
                  <b>{t("register.alert.b-2")}</b>
                </Alert>
              )}
              {errors && !completed && (
                <Alert variant="danger-login">{messageFeedback}</Alert>
              )}
              {captchaValid === false && (
                <Alert variant="danger-login">
                  <b>{t("login.form.captcha.not-valid")}</b>
                </Alert>
              )}
              <Form
                noValidate
                validated={validated}
                className="login-form"
                autoComplete="off"
                onSubmit={handleSubmit}
              >
                <Form.Group>
                  <Form.Label htmlFor="email-field">
                    {t("register.form.email")}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    id="email"
                    placeholder={t("register.form.email")}
                    required
                    isInvalid={formErrors.username}
                    isValid={validated && !formErrors.username}
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                  />
                  <Feedback type="invalid">
                    {t("register.form.email.invalid")}
                  </Feedback>
                </Form.Group>

                <div className="d-flex mt-3 recaptcha">
                  <LocalizedRecaptcha
                    key={`recaptcha-${captchaLanguage}`}
                    ref={captchaRef}
                    sitekey={SITE_KEY}
                    hl={captchaLanguage}
                    onChange={onChange}
                    required
                  />
                </div>
                <div className="d-grid gap-2 mt-5">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="form-login-btn"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? <MiniLoader /> : t("login.form.btn-registrar")}
                  </Button>
                  <p className="login-help-text mt-3">
                    {t("login.help.text")}{" "}
                    <a
                      href={`mailto:${email}`}
                      className="login-help-text-link"
                    >
                      {email}
                    </a>
                  </p>
                </div>
              </Form>
            </Col>
            <Col
              xs={12}
              lg={{ span: 4, offset: 1, order: 3 }}
              className="d-none d-lg-block"
            >
              <Image src={login_img} fluid="true" className="mt-5" />
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
};
